import * as React from "react";
import { toast } from "sonner";
import { Trash2, Play, Square, Plus, X, CheckSquare, Square as SquareIcon, Paperclip, Camera } from "lucide-react";

import DetailDrawer from "@/components/DetailDrawer";
import EvidenceBlock from "@/components/evidence/EvidenceBlock";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * CardDrawer — the RIGHT-side detail drawer (Vaul). Explicitly not a
 * centered modal (that was the reference's pattern; Fable Ops uses right
 * drawers for detail throughout).
 */
export default function CardDrawer({
  open, onOpenChange, card, companyId, boardId, board, lists, labels, deps, cards,
  canEdit, canManage, currentUserId, onUpdated, onDeleted,
}) {
  const [tab, setTab] = React.useState("details");
  const [comments, setComments] = React.useState([]);
  const [commentDraft, setCommentDraft] = React.useState("");
  const [activity, setActivity] = React.useState([]);
  const [timeEntries, setTimeEntries] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!card) return;
    setTab("details");
    // Load members search for the assignee picker (unlimited-ish)
    api.get(`/companies/${companyId}/search/members`, { params: { q: "", limit: 100 } })
       .then(({ data }) => setMembers(data.members || []))
       .catch(() => setMembers([]));
    (async () => {
      const [c, a, t] = await Promise.all([
        api.get(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/comments`),
        api.get(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/activity`),
        api.get(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/time`),
      ]);
      setComments(c.data.comments || []);
      setActivity(a.data.activity || []);
      setTimeEntries(t.data.entries || []);
    })().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  // Live: react to comment/activity/time events for this card
  React.useEffect(() => {
    if (!card) return () => {};
    const on = (e) => {
      const evt = e.detail;
      if (evt?.module !== "boards") return;
      const p = evt.payload || {};
      if (p.card_id !== card.id && p.card?.id !== card.id) return;
      if (evt.type === "comment.created") setComments((prev) => [...prev, p.comment]);
      if (evt.type === "comment.updated") setComments((prev) => prev.map((c) => c.id === p.comment.id ? p.comment : c));
      if (evt.type === "comment.deleted") setComments((prev) => prev.filter((c) => c.id !== p.comment_id));
      if (["timer.started", "timer.stopped", "time.logged"].includes(evt.type)) {
        api.get(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/time`).then(({ data }) => setTimeEntries(data.entries || []));
      }
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  if (!card) return null;

  const currentList = lists.find((l) => l.id === card.list_id);
  const doneList = lists.find((l) => l.is_done);

  async function patch(fields) {
    if (busy) return;
    setBusy(true);
    // Phase 8 — optimistic UI: mutate the card locally FIRST, roll back
    // if the server rejects. Applies to checklist toggle, label toggle,
    // assignee toggle, title/description autosave.
    const previous = { ...card };
    onUpdated && onUpdated({ ...card, ...fields });
    try {
      const { data } = await api.patch(`/companies/${companyId}/boards/${boardId}/cards/${card.id}`, fields);
      onUpdated && onUpdated(data);
    } catch (e) {
      onUpdated && onUpdated(previous);
      const detail = e?.response?.data?.detail;
      if (detail?.code === "evidence_required") {
        // Send the user to the evidence tab so they can attach a photo.
        toast.error(detail.message || "Evidence required to complete this item.");
        setTab("evidence");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Could not save");
      }
    } finally {
      setBusy(false);
    }
  }

  async function moveToList(listId) {
    try {
      const { data } = await api.post(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/move`, {
        target_list_id: listId, target_position: 1e6,
      });
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not move");
    }
  }

  async function toggleChecklistItem(idx) {
    const next = card.checklist.map((it, i) => i === idx
      ? { ...it, done: !it.done, requires_evidence: !!it.requires_evidence } : it);
    patch({ checklist: next });
  }

  async function addChecklistItem(text, requiresEvidence = false) {
    if (!text.trim()) return;
    const next = [...(card.checklist || []),
                  { text: text.trim(), done: false, requires_evidence: !!requiresEvidence }];
    patch({ checklist: next });
  }

  async function toggleRequiresEvidence(idx) {
    const next = card.checklist.map((it, i) => i === idx
      ? { ...it, requires_evidence: !it.requires_evidence } : it);
    patch({ checklist: next });
  }

  async function delChecklistItem(idx) {
    const next = card.checklist.filter((_, i) => i !== idx);
    patch({ checklist: next });
  }

  async function toggleLabel(labelId) {
    const has = (card.label_ids || []).includes(labelId);
    const next = has ? card.label_ids.filter((x) => x !== labelId) : [...(card.label_ids || []), labelId];
    patch({ label_ids: next });
  }

  async function toggleAssignee(uid) {
    const has = (card.assignee_ids || []).includes(uid);
    const next = has ? card.assignee_ids.filter((x) => x !== uid) : [...(card.assignee_ids || []), uid];
    patch({ assignee_ids: next });
  }

  async function sendComment() {
    const body = commentDraft.trim();
    if (!body) return;
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/comments`, { body });
      setCommentDraft("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not comment");
    }
  }

  async function startTimer() {
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/timer/start`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "timer_already_running") {
        toast.error("You already have a running timer. Stop it first.");
      } else {
        toast.error(formatApiErrorDetail(d) || "Could not start timer");
      }
    }
  }
  async function stopTimer() {
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/timer/stop`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not stop timer");
    }
  }
  async function addManualTime(minutes) {
    const m = parseInt(minutes, 10);
    if (!m || m < 1) return;
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/cards/${card.id}/time`, { minutes: m });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not log time");
    }
  }

  async function deleteCard() {
    if (!window.confirm("Delete this card? This cannot be undone by non-manage users.")) return;
    try {
      await api.delete(`/companies/${companyId}/boards/${boardId}/cards/${card.id}`);
      onDeleted && onDeleted(card.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete");
    }
  }

  async function addDependency(blockerId) {
    if (!blockerId || blockerId === card.id) return;
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/dependencies`, {
        blocker_card_id: blockerId, blocked_card_id: card.id,
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "dependency_cycle") toast.error("That would create a dependency cycle.");
      else toast.error(formatApiErrorDetail(d) || "Could not add dependency");
    }
  }
  async function removeDependency(depId) {
    try {
      await api.delete(`/companies/${companyId}/boards/${boardId}/dependencies/${depId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not remove");
    }
  }

  const activeTimer = timeEntries.find((e) => !e.ended_at && e.user_id === currentUserId);
  const totalLogged = timeEntries.filter((e) => e.ended_at).reduce((s, e) => s + (e.minutes || 0), 0);
  const cardDeps = deps.filter((d) => d.blocked_card_id === card.id);
  const cardById = new Map(cards.map((c) => [c.id, c]));
  const doneListIds = new Set(lists.filter((l) => l.is_done).map((l) => l.id));

  const tabs = ["details", "checklist", "comments", "activity", "dependencies", "time", "evidence"];

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={card.title}
      subtitle={currentList ? `In ${currentList.name}` : ""}
      testid="card-drawer"
      actions={
        <>
          {canEdit && !currentList?.is_done && doneList && (
            <button type="button" onClick={() => moveToList(doneList.id)}
                    className="btn-ghost h-8 text-[11.5px]" data-testid="card-drawer-mark-done">
              Mark done
            </button>
          )}
          {canManage && (
            <button type="button" onClick={deleteCard}
                    className="btn-ghost h-8 w-8 !p-0 text-brand-coral"
                    data-testid="card-drawer-delete" title="Delete card">
              <Trash2 size={13} />
            </button>
          )}
        </>
      }
    >
      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 pt-3 border-b border-brand-rule">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-2 py-1.5 text-[11.5px] font-medium border-b-2 transition-colors ${
              tab === t ? "border-brand-violet text-brand-violet" : "border-transparent text-brand-muted hover:text-brand-ink"
            }`}
            data-testid={`card-tab-${t}`}
          >
            {t[0].toUpperCase() + t.slice(1)}
            {t === "comments" && comments.length > 0 && <span className="ml-1 mono-num chip text-[9.5px]">{comments.length}</span>}
            {t === "checklist" && (card.checklist || []).length > 0 && (
              <span className="ml-1 mono-num chip text-[9.5px]">
                {card.checklist.filter((i) => i.done).length}/{card.checklist.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-5 py-4 space-y-5">
        {tab === "details" && (
          <>
            <div>
              <label className="micro-label">Title</label>
              <input
                className="field-input mt-1"
                defaultValue={card.title}
                onBlur={(e) => { if (e.target.value !== card.title && canEdit) patch({ title: e.target.value }); }}
                disabled={!canEdit}
                data-testid="card-title-input"
              />
            </div>
            <div>
              <label className="micro-label">Description</label>
              <textarea
                className="field-input mt-1 py-2 min-h-[120px]"
                defaultValue={card.description}
                onBlur={(e) => { if (e.target.value !== card.description && canEdit) patch({ description: e.target.value }); }}
                disabled={!canEdit}
                data-testid="card-description-input"
              />
            </div>
            <div>
              <label className="micro-label">Due date</label>
              <input
                type="date"
                className="field-input mt-1"
                defaultValue={card.due_date || ""}
                onChange={(e) => { if (canEdit) patch({ due_date: e.target.value || null }); }}
                disabled={!canEdit}
                data-testid="card-due-input"
              />
            </div>
            {/* List move */}
            <div>
              <label className="micro-label">List</label>
              <select
                className="field-input mt-1"
                value={card.list_id}
                onChange={(e) => moveToList(e.target.value)}
                disabled={!canEdit}
                data-testid="card-list-select"
              >
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}{l.is_done ? " · done" : ""}</option>)}
              </select>
            </div>

            {/* Labels */}
            <div>
              <label className="micro-label">Labels</label>
              <div className="mt-1 flex flex-wrap gap-1">
                {labels.map((l) => {
                  const has = (card.label_ids || []).includes(l.id);
                  return (
                    <button
                      key={l.id} type="button"
                      onClick={() => canEdit && toggleLabel(l.id)}
                      className="chip text-[10.5px]"
                      style={has
                        ? { background: `${l.color}22`, color: l.color, borderColor: `${l.color}88` }
                        : { background: "#fff", color: "#6B6584", borderColor: "#EAE3D3" }}
                      data-testid={`card-label-toggle-${l.id}`}
                      disabled={!canEdit}
                    >
                      {l.name}
                    </button>
                  );
                })}
                {labels.length === 0 && <span className="text-[11px] text-brand-faint">No labels on this board.</span>}
              </div>
            </div>

            {/* Assignees */}
            <div>
              <label className="micro-label">Assignees</label>
              <div className="mt-1 max-h-[160px] overflow-y-auto space-y-1">
                {members.map((m) => {
                  const has = (card.assignee_ids || []).includes(m.id);
                  return (
                    <button
                      key={m.id} type="button"
                      onClick={() => canEdit && toggleAssignee(m.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded-md ${has ? "bg-accent" : "hover:bg-brand-hush"}`}
                      disabled={!canEdit}
                      data-testid={`card-assign-toggle-${m.id}`}
                    >
                      <span className={`h-4 w-4 rounded ${has ? "bg-brand-violet text-white" : "bg-brand-hush text-brand-muted"} flex items-center justify-center text-[9px]`}>
                        {has ? "✓" : ""}
                      </span>
                      <span className="text-[12px] font-medium text-brand-ink">{m.name}</span>
                      <span className="text-[11px] text-brand-muted truncate">{m.email}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {tab === "checklist" && (
          <div>
            <label className="micro-label">Checklist</label>
            <div className="mt-2 space-y-1">
              {(card.checklist || []).map((it, i) => (
                <div key={it.id || i} className="flex items-center gap-2 group"
                     data-testid={`card-check-row-${i}`}>
                  <button type="button" onClick={() => canEdit && toggleChecklistItem(i)}
                          className="h-4 w-4 shrink-0" disabled={!canEdit}
                          data-testid={`card-check-toggle-${i}`}>
                    {it.done
                      ? <CheckSquare size={16} className="text-brand-violet" />
                      : <SquareIcon size={16} className="text-brand-muted" />}
                  </button>
                  <span className={`flex-1 text-[13px] ${it.done ? "line-through text-brand-muted" : "text-brand-ink"}`}>
                    {it.text}
                  </span>
                  {it.requires_evidence && (
                    <span className={`chip text-[9.5px] mono-num flex items-center gap-0.5 ${it.done ? "" : "chip-amber"}`}
                          data-testid={`card-check-evidence-req-${i}`}
                          title="Evidence required to mark this item done.">
                      <Camera size={9}/> evidence
                    </span>
                  )}
                  {canEdit && (
                    <>
                      <button type="button" onClick={() => toggleRequiresEvidence(i)}
                              className={`opacity-0 group-hover:opacity-100 btn-ghost h-6 !p-1 text-[10px] ${it.requires_evidence ? "text-brand-violet" : "text-brand-muted"}`}
                              title="Toggle 'requires evidence'"
                              data-testid={`card-check-evidence-toggle-${i}`}>
                        <Paperclip size={11}/>
                      </button>
                      <button type="button" onClick={() => delChecklistItem(i)}
                              className="opacity-0 group-hover:opacity-100 btn-ghost h-6 w-6 !p-0"
                              data-testid={`card-check-del-${i}`}>
                        <X size={11} />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {canEdit && (
                <ChecklistAdd onAdd={addChecklistItem} />
              )}
            </div>
          </div>
        )}

        {tab === "comments" && (
          <div>
            <label className="micro-label">Comments</label>
            <div className="mt-2 space-y-3">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`card-comment-${c.id}`}>
                  <div className="h-6 w-6 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                    {(c.author_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] text-brand-muted">
                      <span className="font-semibold text-brand-ink">{c.author_name}</span>
                      <span className="mono-num ml-2">{new Date(c.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
                      {c.edited_at && <span className="ml-2 mono-num text-[10px]">edited</span>}
                    </div>
                    <div className="text-[13px] text-brand-ink whitespace-pre-wrap">{c.body}</div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && <div className="text-[12px] text-brand-muted">No comments yet.</div>}
              {canEdit && (
                <div>
                  <textarea
                    className="field-input py-2 min-h-[64px] w-full"
                    placeholder="Write a comment…"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    data-testid="card-comment-input"
                  />
                  <div className="mt-1 flex justify-end">
                    <button type="button" onClick={sendComment} disabled={!commentDraft.trim()}
                            className="btn-violet" data-testid="card-comment-send">
                      Comment
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "activity" && (
          <div>
            <label className="micro-label">Activity</label>
            <div className="mt-2 space-y-2">
              {activity.length === 0 && <div className="text-[12px] text-brand-muted">No activity yet.</div>}
              {activity.map((a) => (
                <div key={a.id} className="text-[12px] text-brand-ink" data-testid={`card-activity-${a.id}`}>
                  <span className="font-semibold">{a.actor_name || "system"}</span>
                  <span className="text-brand-muted"> · </span>
                  <span className="mono-num text-[10.5px] text-brand-muted">{a.action}</span>
                  <span className="text-brand-muted mono-num text-[10.5px] ml-2">
                    {new Date(a.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "dependencies" && (
          <div>
            <label className="micro-label">Blocked by</label>
            <div className="mt-2 space-y-1.5">
              {cardDeps.length === 0 && <div className="text-[12px] text-brand-muted">No dependencies.</div>}
              {cardDeps.map((d) => {
                const b = cardById.get(d.blocker_card_id);
                const blockerDone = b && doneListIds.has(b.list_id);
                return (
                  <div key={d.id} className="flex items-center gap-2" data-testid={`card-dep-row-${d.id}`}>
                    <span className={`chip ${blockerDone ? "chip-emerald" : "chip-coral"} text-[10.5px]`}>
                      {blockerDone ? "resolved" : "blocking"}
                    </span>
                    <span className="text-[12.5px] text-brand-ink truncate flex-1">
                      {b ? b.title : "(missing card)"}
                    </span>
                    {canEdit && (
                      <button type="button" onClick={() => removeDependency(d.id)}
                              className="btn-ghost h-6 w-6 !p-0" data-testid={`card-dep-remove-${d.id}`}>
                        <X size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
              {canEdit && (
                <DependencyAdd
                  cards={cards.filter((c) => c.board_id === boardId && c.id !== card.id && !c.archived)}
                  onAdd={addDependency}
                />
              )}
            </div>
          </div>
        )}

        {tab === "time" && (
          <div>
            <label className="micro-label">Time tracking</label>
            <div className="mt-2 flex items-center gap-2">
              {activeTimer ? (
                <button type="button" onClick={stopTimer} className="btn-violet" data-testid="card-timer-stop">
                  <Square size={13} /> Stop timer
                </button>
              ) : (
                <button type="button" onClick={startTimer} disabled={!canEdit} className="btn-violet"
                        data-testid="card-timer-start">
                  <Play size={13} /> Start timer
                </button>
              )}
              <ManualTimeInput onAdd={addManualTime} disabled={!canEdit} />
              <div className="ml-auto text-[11px] text-brand-muted">
                Total: <span className="mono-num text-brand-ink">{Math.floor(totalLogged / 60)}h {totalLogged % 60}m</span>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {timeEntries.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-[11.5px]" data-testid={`card-time-row-${e.id}`}>
                  <span className={`chip text-[9.5px] mono-num ${e.source === "timer" ? "chip-violet" : ""}`}>
                    {e.source}
                  </span>
                  <span className="text-brand-ink">{e.user_name}</span>
                  <span className="mono-num text-brand-muted">
                    {e.minutes != null ? `${e.minutes}m` : "running…"}
                  </span>
                  <span className="ml-auto mono-num text-[10.5px] text-brand-faint">
                    {new Date(e.started_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "evidence" && (
          <EvidenceBlock
            companyId={companyId}
            attachedType="card"
            attachedId={card.id}
            canEdit={canEdit}
            testidPrefix="card-evidence"
          />
        )}
      </div>
    </DetailDrawer>
  );
}

function ChecklistAdd({ onAdd }) {
  const [val, setVal] = React.useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onAdd(val); setVal(""); }}
      className="flex items-center gap-2 mt-2"
    >
      <input
        className="field-input h-8 flex-1"
        placeholder="Add an item…"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid="card-check-add-input"
      />
      <button type="submit" className="btn-outline h-8" data-testid="card-check-add-submit">
        <Plus size={12} /> Add
      </button>
    </form>
  );
}

function ManualTimeInput({ onAdd, disabled }) {
  const [val, setVal] = React.useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onAdd(val); setVal(""); }}
      className="flex items-center gap-1"
    >
      <input
        className="field-input h-8 w-20"
        placeholder="min"
        value={val}
        onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ""))}
        disabled={disabled}
        data-testid="card-time-manual-input"
      />
      <button type="submit" className="btn-outline h-8" disabled={disabled || !val}
              data-testid="card-time-manual-submit">
        + Log
      </button>
    </form>
  );
}

function DependencyAdd({ cards, onAdd }) {
  const [pick, setPick] = React.useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onAdd(pick); setPick(""); }}
          className="flex items-center gap-2 mt-2">
      <select className="field-input h-8 flex-1"
              value={pick} onChange={(e) => setPick(e.target.value)}
              data-testid="card-dep-add-select">
        <option value="">Pick a blocker…</option>
        {cards.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
      </select>
      <button type="submit" className="btn-outline h-8" disabled={!pick}
              data-testid="card-dep-add-submit">
        <Plus size={12} /> Add
      </button>
    </form>
  );
}
